import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from "react";
import { supabase } from "../lib/supabase";
import { COACH_WORKOUT_ENGINE } from "../lib/coachEngine";
import type { ExerciseDraft } from "../lib/builder";
import { shelfMatches } from "../lib/exerciseShelf";
import { Icon } from "./Icon";
import {
  CHAIN_LABEL, PROGRESS_DEFAULT, choiceDetail, choiceShort, effectiveRule, engineKey, isLadder, ladderJson,
  TWICE, chainSummary, ladderPicks, ladderUnits, linkComplete, parseLadder, ruleChoices, rowRuleLine, rowRuleShort,
  type LadderLink, type LadderUnit, type LinkDraft, type RuleChoice, type WorkoutEngine,
} from "../lib/workoutEngine";

/** What the ladder needs of a library row: its name for the line and the picker, and what it tracks for the targets. */
export type LadderExercise = { id: string; name: string; trackingFields: readonly string[]; isHold: boolean; hiddenAt?: string | null };

/* 🔴 F15 STEP 3, WORKOUT-BASED — the switch and the per-row line in the workout editor (his 2026-10-30 evening word: *"on/off
   for the engine and if on to just select how each will progress"*). The rules and the merge are `lib/workoutEngine.ts`;
   this file is the two tables' reads and writes and the two controls.

   ⚠️ WRITES ARE IMMEDIATE, ONE PER GESTURE, like `AutoApplySwitch` — not part of the draft's Save. The draft's save is
   replace-not-merge over SECTIONS; these rows hang on the ROOT workout and a library exercise, so no save can touch them
   and holding them in the draft would only invent a second copy of a database fact. A refused write shows the server's
   own sentence and the control keeps its old state. */

export type Loaded = WorkoutEngine & { isCopy: boolean };

export function useWorkoutEngine(workoutId: string | null): {
  engine: Loaded | null;
  error: string | null;
  setRule: (exerciseId: string, choice: RuleChoice) => Promise<boolean>;
} {
  const [engine, setEngine] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEngine(null);
    /* Hidden (`COACH_WORKOUT_ENGINE`): nothing is read, nothing is drawn. */
    if (!COACH_WORKOUT_ENGINE || workoutId === null) return;
    let live = true;
    void (async () => {
      const w = await supabase.from("coach_workouts").select("id, root_workout_id").eq("id", workoutId).maybeSingle();
      if (!live || w.data === null) return;
      const key = engineKey({ id: w.data.id, rootWorkoutId: w.data.root_workout_id });
      const r = await supabase.from("coach_workout_rules").select("exercise_id, progression_config").eq("workout_id", key);
      if (!live) return;
      if (r.error !== null) { setError(r.error.message); return; }
      /* F15 4.2 — the switch and the cadence moved to the PROGRAM (his 10-31 word): the editor draws every rule, ON. */
      setEngine({
        key,
        isCopy: w.data.root_workout_id !== null,
        progress: PROGRESS_DEFAULT,
        deloadEvery: null,
        rules: new Map((r.data ?? []).map((row) => [row.exercise_id, row.progression_config])),
      });
    })();
    return () => { live = false; };
  }, [workoutId]);


  /* F15 4.1a: says whether it was written (a tab the editor wrote and the server refused goes back to the stored list), and
     merges into the LATEST rules — the tabs write after a pause in typing, so the engine it began from may be stale. */
  async function setRule(exerciseId: string, choice: RuleChoice): Promise<boolean> {
    if (engine === null) return false;
    setError(null);
    const { error: err } = choice.key === "library"
      ? await supabase.from("coach_workout_rules").delete().eq("workout_id", engine.key).eq("exercise_id", exerciseId)
      : await supabase.from("coach_workout_rules").upsert({
          workout_id: engine.key, exercise_id: exerciseId,
          progression_config: choice.config as never, updated_at: new Date().toISOString(),
        }, { onConflict: "workout_id,exercise_id" });
    if (err !== null) { setError(err.message); return false; }
    setEngine((e) => {
      if (e === null) return e;
      const rules = new Map(e.rules);
      if (choice.key === "library") rules.delete(exerciseId); else rules.set(exerciseId, choice.config);
      return { ...e, rules };
    });
    return true;
  }

  return { engine, error, setRule };
}

/* 🔴 F15 STEP 3.5 — NO NATIVE DROPDOWN IN THESE ROWS (Fable, 2026-10-30 night: the ladder's `<select>`s and the system menu
   under the row line read as a browser's, not ours). The three pop-ups — a row's rule, the deload cadence, a link's next
   exercise — are the builder's own menu surface (`.builder-section-menu`, the row's `⋯` and the section's menu), real buttons
   in a real list. One dismissal shape for all three: Escape is kept INSIDE (the builder is a `<dialog>` whose Escape closes
   the window — `AddSetSplit`'s rule), a press outside closes, and focus goes back to the button that opened it. Round 2: pop-ups
   NEST (the ladder's editor holds the rule menu and the exercise search), so Escape closes only the INNERMOST open one — the
   last pushed on `openPopovers`; every listener sits on the document, so the stack, not propagation, decides. */
const openPopovers: object[] = [];
function usePopover(open: boolean, close: () => void, root: RefObject<HTMLElement>, trigger: RefObject<HTMLButtonElement>) {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return;
    const me = {};
    openPopovers.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || openPopovers[openPopovers.length - 1] !== me) return;
      e.stopPropagation();
      e.preventDefault();
      closeRef.current();
      trigger.current?.focus();
    };
    const onDown = (e: MouseEvent) => {
      if (root.current !== null && e.target instanceof Node && root.current.contains(e.target)) return;
      closeRef.current();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown);
    return () => {
      openPopovers.splice(openPopovers.indexOf(me), 1);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, root, trigger]);
}

/** Up/Down walk a pop-up's buttons, wrapping; the first/last step from the search field is the caller's. */
function walk(e: ReactKeyboardEvent<HTMLElement>, list: HTMLElement | null) {
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  e.preventDefault();
  const all = [...(list?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
  const at = all.indexOf(document.activeElement as HTMLButtonElement);
  all[(at + (e.key === "ArrowDown" ? 1 : all.length - 1)) % all.length]?.focus();
}

export type PickItem = { key: string; label: string; detail?: string | null };

/** A line's words with the chevron held to the LAST word (round 2): a sentence that wraps never strands the chevron alone. */
function PickWords({ text }: { text: string }) {
  const cut = text.lastIndexOf(" ") + 1;
  return <>{text.slice(0, cut)}<span className="wb-rule-pick-tail">{text.slice(cut)}</span></>;
}

/** A line that reads as its words with a chevron, and the menu that changes it: each item its short words, then the whole
 *  rule on a second line — the row says it at a glance, the menu says all of it. */
export function EnginePick({ name, shown, items, value, onPick, className }: {
  name: string; shown: string; items: readonly PickItem[]; value: string; onPick: (key: string) => void; className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  usePopover(open, () => setOpen(false), root, button);
  useEffect(() => {
    if (!open) return;
    (list.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]') ?? list.current?.querySelector("button"))?.focus();
  }, [open]);
  return (
    <span className={className === undefined ? "wb-rule-pick-wrap" : `wb-rule-pick-wrap ${className}`} ref={root}>
      <button ref={button} type="button" className="wb-rule-pick" aria-haspopup="menu" aria-expanded={open}
              aria-label={`${name}: ${shown}`} onClick={() => setOpen((o) => !o)}>
        <PickWords text={shown} />
      </button>
      {open && (
        <ul ref={list} className="builder-section-menu wb-rule-menu" role="menu" aria-label={name} onKeyDown={(e) => walk(e, list.current)}>
          {items.map((it) => (
            <li key={it.key} role="none">
              <button type="button" role="menuitemradio" aria-checked={it.key === value}
                      onClick={() => { setOpen(false); button.current?.focus(); if (it.key !== value) onPick(it.key); }}>
                <span className="wb-rule-menu-check" aria-hidden="true">{it.key === value ? "✓" : ""}</span>
                <span className="wb-rule-menu-words">
                  <span className="wb-rule-menu-label">{it.label}</span>
                  {it.detail != null && <span className="wb-rule-menu-detail">{it.detail}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}

/** One row's line: what happens next week, and the menu that changes it. Null while the switch is off. Step 3.4: a row whose
 *  OWN rule is a ladder draws its links under the line. */
export function RowRuleLine({ engine, exercise, libraryConfig, library, onPick }: {
  engine: Loaded; exercise: ExerciseDraft; libraryConfig: unknown; library: readonly LadderExercise[];
  onPick: (choice: RuleChoice) => void;
}) {
  const names = useMemo(() => new Map(library.map((e) => [e.id.toLowerCase(), e.name])), [library]);
  if (!engine.progress) return null;
  const eff = effectiveRule(engine, exercise.libraryExerciseId, libraryConfig);
  const choices = ruleChoices(exercise.sets);
  const ladder = eff.source === "workout" && isLadder(eff.config);
  const current: RuleChoice | null = eff.source === "workout"
    ? (ladder ? choices.find((c) => c.key === "chain")! : undefined)
      ?? choices.find((c) => c.key !== "library" && JSON.stringify(c.config) === JSON.stringify(eff.config))
      ?? { key: "none", config: eff.config }
    : null;
  /* A workout rule written with other numbers than today's dose offers is kept as its own first option, never re-mapped. */
  const extra = current !== null && !choices.includes(current) && eff.config !== null;
  const value = eff.source === "library" ? "library" : extra ? "current" : current!.key;
  const short = rowRuleShort(eff, names);
  /* The editor only on the row that OWNS the chain. Round 2 (Fable: an always-open editor is a wall in a 15-row workout): that
     row's line is the chain's one-line sentence and OPENS the editor as a pop-up, the way the rule line opens its menu; the
     editor's header is the rule menu itself ("Next exercise at a target"). A row a ladder moved to reads the NEXT step of the
     rest of its chain; the whole chain is the menu's second line. */
  const own = ladder && engine.rules.has(exercise.libraryExerciseId);
  const shown = own ? CHAIN_LABEL : short.text;
  const summary = own ? chainSummary(eff.config, names) : short;
  const items: PickItem[] = [
    ...(extra ? [{ key: "current", label: short.text, detail: rowRuleLine(eff, names).text }] : []),
    ...choices.map((c) => ({
      key: c.key, label: choiceShort(c, libraryConfig),
      detail: c.key === "chain" && ladder && !own ? rowRuleLine(eff, names).text : choiceDetail(c, libraryConfig),
    })),
  ];
  const pick = (
    <EnginePick name={`How ${exercise.name} progresses`} shown={shown} items={items} value={value}
                onPick={(key) => {
                  const picked = choices.find((c) => c.key === key);
                  if (picked !== undefined) onPick(picked);
                }} />
  );
  return (
    <span className="wb-rule-line wb-rule-pick-line">
      {own ? (
        <LadderPop name={`${exercise.name}: next exercises`} summary={summary.text}>
          <span className="wb-ladder-head">{pick}</span>
          <LadderEditor rowExerciseId={exercise.libraryExerciseId} rowName={exercise.name} config={eff.config}
                        library={library}
                        onChange={(links) => onPick({ key: "chain", config: ladderJson(links) })} />
        </LadderPop>
      ) : pick}
      {summary.lacks !== null && <span className="wb-rule-lacks"> — {summary.lacks}</span>}
    </span>
  );
}

/** The owning row's line (round 2): the chain in one sentence, CLOSED by default; a click opens the editor under it as a pop-up
 *  on the builder's menu surface — Escape, a press outside or the line again closes it. A half-written link (a draft, never
 *  written) goes with it. */
function LadderPop({ name, summary, children }: { name: string; summary: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  usePopover(open, () => setOpen(false), root, button);
  useEffect(() => { if (open) panel.current?.querySelector<HTMLElement>("button, input")?.focus(); }, [open]);
  return (
    <span className="wb-rule-pick-wrap wb-ladder-pop-wrap" ref={root}>
      <button ref={button} type="button" className="wb-rule-pick" aria-haspopup="dialog" aria-expanded={open}
              aria-label={`${name}: ${summary}`} onClick={() => setOpen((o) => !o)}>
        <PickWords text={summary} />
      </button>
      {open && <div ref={panel} className="wb-ladder-pop" role="dialog" aria-label={name}>{children}</div>}
    </span>
  );
}

const UNIT_WORD: Record<LadderUnit, string> = { reps: "reps", kg: "kg", seconds: "s" };

/** A link's next exercise: the name in full (it wraps, never cut), and a pop-up search over the library — the builder's own
 *  search field (`.builder-search`) over the same matcher the library pane uses (`shelfMatches`). */
export function ExercisePick({ value, picks, label, onPick }: {
  value: string | null; picks: readonly LadderExercise[]; label: string; onPick: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  usePopover(open, () => setOpen(false), root, button);
  useEffect(() => { if (open) { setQ(""); input.current?.focus(); } }, [open]);
  const shown = value === null ? null : picks.find((p) => p.id.toLowerCase() === value)?.name ?? "A deleted exercise";
  const found = picks.filter((p) => shelfMatches(p, q));
  function pick(id: string) {
    setOpen(false);
    button.current?.focus();
    if (id.toLowerCase() !== value) onPick(id.toLowerCase());
  }
  return (
    <span className="wb-ladder-pick-wrap" ref={root}>
      <button ref={button} type="button" className={shown === null ? "wb-ladder-pick is-empty" : "wb-ladder-pick"}
              aria-haspopup="dialog" aria-expanded={open} aria-label={`${label}: ${shown ?? "none chosen"}`}
              onClick={() => setOpen((o) => !o)}>
        {shown ?? "Choose exercise"}
      </button>
      {open && (
        <div className="builder-section-menu wb-pick-menu" role="dialog" aria-label={label}>
          <div className="builder-search wb-pick-search">
            <Icon name="search" className="builder-search-glass" size={16} />
            <input ref={input} className="ht-input builder-search-input" value={q} placeholder="Search exercises"
                   aria-label="Search exercises" onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => {
                     if (e.key === "Enter") { e.preventDefault(); const first = found[0]; if (first !== undefined) pick(first.id); }
                     if (e.key === "ArrowDown") { e.preventDefault(); list.current?.querySelector("button")?.focus(); }
                   }} />
          </div>
          <ul ref={list} className="wb-pick-list" onKeyDown={(e) => {
                if (e.key === "ArrowUp" && document.activeElement === list.current?.querySelector("button")) {
                  e.preventDefault(); input.current?.focus(); return;
                }
                walk(e, list.current);
              }}>
            {found.map((p) => (
              <li key={p.id}>
                <button type="button" aria-pressed={p.id.toLowerCase() === value} onClick={() => pick(p.id)}>{p.name}</button>
              </li>
            ))}
          </ul>
          {found.length === 0 && <p className="ht-caption wb-pick-none">Nothing matches “{q}”.</p>}
        </div>
      )}
    </span>
  );
}

/* 🔴 F15 STEP 3.4 — THE LINK EDITOR, laid as TWO LINES per link since step 3.5 (Fable: "Advanced Tuck Front Le…" was cut):
   "At [8] reps [25] kg  [twice in a row]  ×", then "→ Advanced Tuck Front Lever" — the target, then where it goes, at every
   width, so a name is never truncated. Round 2: ONE toggle chip (off = once); ONE × per link at the link's right edge on the
   first line, so the ×s line up down the editor. The target is measured on the exercise BEFORE the arrow (the row's own for the
   first link), in the units IT tracks. A link is WRITTEN only once it names an exercise and a target; until then it is a
   draft here. Every finished gesture writes the whole chain (one row, `coach_workout_rules`). Add and remove are the
   builder's own: `Add Set`'s shape, the set row's ×. */
function LadderEditor({ rowExerciseId, rowName, config, library, onChange }: {
  rowExerciseId: string; rowName: string; config: unknown; library: readonly LadderExercise[];
  onChange: (links: LadderLink[]) => void;
}) {
  const stored = parseLadder(config)?.links ?? [];
  const [draft, setDraft] = useState<LinkDraft | null>(null);
  const byId = useMemo(() => new Map(library.map((e) => [e.id.toLowerCase(), e])), [library]);
  const all: LinkDraft[] = draft === null ? stored : [...stored, draft];
  const measuredOn = (i: number) => byId.get(i === 0 ? rowExerciseId.toLowerCase() : (all[i - 1]?.next ?? ""));
  const nameOf = (i: number) => (i === 0 ? rowName : (byId.get(all[i - 1]?.next ?? "")?.name ?? "it"));

  function commit(i: number, next: LinkDraft) {
    if (i < stored.length) {
      if (!linkComplete(next)) return;                      // a stored link never loses its exercise or its last target
      onChange(stored.map((l, j) => (j === i ? { ...next, next: next.next } : l)));
      return;
    }
    if (linkComplete(next)) { setDraft(null); onChange([...stored, { ...next, next: next.next }]); } else setDraft(next);
  }
  function remove(i: number) {
    if (i >= stored.length) { setDraft(null); return; }
    setDraft(null);
    onChange(stored.slice(0, i));                           // a later link is measured on this one: it goes with it
  }

  return (
    <span className="wb-ladder" role="group" aria-label={`${rowName}: next exercises`}>
      {all.map((link, i) => {
        const units = ladderUnits(measuredOn(i));
        const picks = ladderPicks(library, rowExerciseId, all.filter((_, j) => j !== i), link.next);
        return (
          <span className="wb-ladder-link" key={i}>
            <span className="wb-ladder-line wb-ladder-target">
              <span className="wb-ladder-at">At</span>
              {units.map((u) => (
                <label className="wb-ladder-num" key={u}>
                  <TargetField value={link[u]} unit={u} label={`${nameOf(i)} target, ${UNIT_WORD[u]}`}
                               onCommit={(v) => commit(i, { ...link, [u]: v })} />
                  <span>{UNIT_WORD[u]}</span>
                </label>
              ))}
              <button type="button" className="ours-chip wb-ladder-twice" aria-pressed={link.sessions === 2}
                      onClick={() => commit(i, { ...link, sessions: link.sessions === 2 ? 1 : 2 })}>
                {TWICE}
              </button>
              <button type="button" className="ht-button ht-button-quiet wb-ladder-remove" aria-label="Remove this step"
                      onClick={() => remove(i)}>×</button>
            </span>
            <span className="wb-ladder-line wb-ladder-move">
              <span className="wb-ladder-arrow" aria-hidden="true">→</span>
              <ExercisePick value={link.next} picks={picks} label="Next exercise"
                            onPick={(id) => commit(i, { ...link, next: id })} />
            </span>
          </span>
        );
      })}
      {draft === null && (stored.length === 0 || byId.has(stored[stored.length - 1]!.next)) && (
        <span className="addset wb-addset wb-ladder-add">
          <button type="button" className="addset-one"
                  onClick={() => setDraft({ next: null, reps: null, kg: null, seconds: null, sessions: 1 })}>
            Add next exercise
          </button>
        </span>
      )}
    </span>
  );
}

/** A whole number (reps, seconds) or a load in kilos; written on blur or Return, never per keystroke. Blank = no target. */
export function TargetField({ value, unit, label, onCommit }: {
  value: number | null; unit: LadderUnit; label: string; onCommit: (v: number | null) => void;
}) {
  const [text, setText] = useState(value === null ? "" : String(value));
  useEffect(() => { setText(value === null ? "" : String(value)); }, [value]);
  function done() {
    const t = text.trim().replace(",", ".");
    if (t === "") { if (value !== null) onCommit(null); return; }
    const n = Number(t);
    const ok = Number.isFinite(n) && n > 0 && (unit === "kg" || Number.isInteger(n));
    if (!ok) { setText(value === null ? "" : String(value)); return; }
    if (n !== value) onCommit(n);
  }
  return (
    <input className="wb-ladder-input" inputMode={unit === "kg" ? "decimal" : "numeric"} aria-label={label} value={text}
           onChange={(e) => setText(e.target.value)} onBlur={done}
           onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); done(); } }} />
  );
}
